import { useMemo, useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Check, X, Edit, AlertTriangle, RotateCcw, ChevronLeft, ChevronRight } from 'lucide-react'
import { useDiffComputation } from '@/hooks/use-diff-computation'
import { UnifiedDiffView } from '@/components/features/unified-diff-view'
import { DiffScrollPane } from '@/components/features/diff-scroll-pane'
import {
  buildSplitDiffOverviewMarkers,
  buildUnifiedDiffOverviewMarkers,
  changeIncludesLine,
} from '@/lib/diff-overview-markers'
import { normalizeForDriveCompare } from '@/lib/google-drive-diff-normalize'
import { testIds } from '@/lib/test-ids'
import { cn } from '@/lib/utils'
import '@git-diff-view/react/styles/diff-view.css'

/** Same-minute timestamps are ambiguous in the UI - do not crown a "newer" side. */
const NEWER_TIE_MS = 60_000

function formatConflictWhen(iso?: string | null): string {
  if (!iso) return 'unknown time'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return 'unknown time'
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

function newerConflictSide(
  leftIso?: string | null,
  rightIso?: string | null,
): 'left' | 'right' | 'unknown' {
  if (!leftIso || !rightIso) return 'unknown'
  const left = new Date(leftIso).getTime()
  const right = new Date(rightIso).getTime()
  if (Number.isNaN(left) || Number.isNaN(right)) return 'unknown'
  if (Math.abs(left - right) <= NEWER_TIE_MS) return 'unknown'
  return left > right ? 'left' : 'right'
}

export interface DiffPreviewProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  original: string
  proposed: string
  filename: string
  /**
   * Dialog heading override. When omitted, a sensible default is chosen based on mode:
   * "Resolve Conflict" (conflictMode), "Revision diff" (readOnly), or "Review Proposed
   * Changes" (default AI-edit-review case). Callers with mode-specific context (e.g. a
   * revision timestamp) should pass an explicit title rather than relying on the default.
   */
  title?: string
  /** Required unless readOnly - the readOnly view has no accept/reject actions. */
  onAccept?: () => void
  onReject?: () => void
  onEdit?: () => void
  isApplying?: boolean
  /** Three-way inbox conflict mode (upstream vs FlowNotes DB) */
  conflictMode?: boolean
  /**
   * Conflict framing. `git` = inbox upstream merge. `drive` = Keep synced / choose
   * which side is the source of truth (Maater vs Google Drive).
   */
  conflictVariant?: 'git' | 'drive'
  /** Left-column last-modified ISO. Drive conflict: Google Drive modifiedTime. */
  leftUpdatedAt?: string | null
  /** Right-column last-modified ISO. Drive conflict: Maater updated_at. */
  rightUpdatedAt?: string | null
  onAcceptUpstream?: () => void
  onKeepFlowNotes?: () => void
  /** Conflict mode: dismiss without choosing (defaults to closing the dialog). */
  onCancel?: () => void
  onAcceptAI?: (aiContent: string) => void
  /** When set alongside conflictMode and onAcceptAI, shows Accept AI proposal */
  aiProposal?: string
  /** Read-only historical diff view: hides Accept/Reject/Edit First, shows a single Close button. onAccept/onReject/onEdit are ignored when true. */
  readOnly?: boolean
  /** Read-only mode only: when set, renders a "Restore this version" button next to Close. */
  onRestore?: () => void
  /** Disables the restore button and shows a busy label while the restore request is in flight. */
  restoring?: boolean
  /** When reviewing multiple proposed edits in one session. */
  batchIndex?: number
  batchTotal?: number
  onBatchPrevious?: () => void
  onBatchNext?: () => void
}

export function DiffPreviewDialog({
  open,
  onOpenChange,
  original,
  proposed,
  filename,
  title,
  onAccept,
  onReject,
  onEdit,
  isApplying = false,
  conflictMode = false,
  conflictVariant = 'git',
  leftUpdatedAt = null,
  rightUpdatedAt = null,
  onAcceptUpstream,
  onKeepFlowNotes,
  onCancel,
  onAcceptAI,
  aiProposal,
  readOnly = false,
  onRestore,
  restoring = false,
  batchIndex,
  batchTotal,
  onBatchPrevious,
  onBatchNext,
}: DiffPreviewProps) {
  const [viewType, setViewType] = useState<'split' | 'unified'>('split')
  const isDriveConflict = conflictMode && conflictVariant === 'drive'
  const newerSide = newerConflictSide(leftUpdatedAt, rightUpdatedAt)
  const leftWhen = formatConflictWhen(leftUpdatedAt)
  const rightWhen = formatConflictWhen(rightUpdatedAt)

  // Drive chooser: quiet formatting churn in the visual diff only. Keep/Use still use raw props.
  const diffOriginal = useMemo(
    () => (isDriveConflict ? normalizeForDriveCompare(original) : original),
    [isDriveConflict, original],
  )
  const diffProposed = useMemo(
    () => (isDriveConflict ? normalizeForDriveCompare(proposed) : proposed),
    [isDriveConflict, proposed],
  )
  const hidingFormatNoise =
    isDriveConflict && (diffOriginal !== original || diffProposed !== proposed)

  const dialogTitle =
    title ??
    (isDriveConflict
      ? 'Sync enabled - which version is true?'
      : conflictMode
        ? 'Resolve Conflict'
        : readOnly
          ? 'Revision diff'
          : batchTotal != null && batchTotal > 1 && batchIndex != null
            ? `Review proposed changes (${batchIndex + 1} of ${batchTotal})`
            : 'Review Proposed Changes')

  const leftColumnLabel = isDriveConflict
    ? `Google Drive · modified ${leftWhen}`
    : conflictMode
      ? 'Maater (your edits)'
      : 'Original'
  const rightColumnLabel = isDriveConflict
    ? `Maater · updated ${rightWhen}`
    : conflictMode
      ? 'Upstream (git)'
      : 'Proposed'

  const dateHint =
    isDriveConflict && newerSide === 'right'
      ? `Maater is newer (${rightWhen} vs Drive ${leftWhen}). Green on the right is what Keep Maater would write to Drive.`
      : isDriveConflict && newerSide === 'left'
        ? `Google Drive is newer (${leftWhen} vs Maater ${rightWhen}). Keep Maater still writes the right column to Drive.`
        : isDriveConflict
          ? `Compare timestamps: Drive ${leftWhen} · Maater ${rightWhen}. Green on the right is the Keep Maater push.`
          : null

  const handleCancel = () => {
    if (onCancel) onCancel()
    else onOpenChange(false)
  }

  const showBatchNav =
    !readOnly &&
    !conflictMode &&
    batchTotal != null &&
    batchTotal > 1 &&
    batchIndex != null &&
    onBatchPrevious &&
    onBatchNext

  const { additions, deletions, changes, isDestructive, isMajorDeletion, deletionPercentage, normalizedOriginal, normalizedProposed } =
    useDiffComputation(diffOriginal, diffProposed, filename)

  const splitMarkers = useMemo(
    () => buildSplitDiffOverviewMarkers(normalizedOriginal, normalizedProposed, changes),
    [normalizedOriginal, normalizedProposed, changes]
  )

  const unifiedMarkers = useMemo(
    () => buildUnifiedDiffOverviewMarkers(changes),
    [changes]
  )

  const originalLines = useMemo(() => normalizedOriginal.split('\n'), [normalizedOriginal])
  const proposedLines = useMemo(() => normalizedProposed.split('\n'), [normalizedProposed])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="wide" className="flex flex-col p-0">
        <DialogHeader className="pl-6 pr-12 pt-6 pb-4 border-b">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-xl font-semibold">
                {dialogTitle}
              </DialogTitle>
              <DialogDescription className="mt-1">
                {isDriveConflict ? (
                  <span className="block space-y-1">
                    <span className="block text-sm text-muted-foreground">
                      Keep synced is on. Maater and Google Drive differ - pick which document is the source of truth.
                    </span>
                    {dateHint ? (
                      <span className="block text-sm font-medium text-foreground">{dateHint}</span>
                    ) : null}
                    {hidingFormatNoise ? (
                      <span className="block text-xs text-muted-foreground">
                        Diff hides formatting-only noise (line breaks, list markers). Keep / Use still apply the full document.
                      </span>
                    ) : null}
                    <span className="block truncate text-xs text-muted-foreground">{filename}</span>
                  </span>
                ) : (
                  <span className="block truncate">{filename}</span>
                )}
              </DialogDescription>
            </div>

            <div
              className="inline-flex items-center rounded-md border border-border p-0.5 ml-4 shrink-0"
              role="tablist"
              aria-label="Diff view mode"
              data-testid={testIds.diffPreviewDialog.viewModeTabs}
            >
              <button
                type="button"
                role="tab"
                aria-selected={viewType === 'split'}
                className={cn(
                  'px-3 py-1 text-xs rounded-sm transition-colors',
                  viewType === 'split'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                )}
                onClick={() => setViewType('split')}
              >
                Split
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={viewType === 'unified'}
                className={cn(
                  'px-3 py-1 text-xs rounded-sm transition-colors',
                  viewType === 'unified'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                )}
                onClick={() => setViewType('unified')}
              >
                Unified
              </button>
            </div>
          </div>

          <div className="flex items-center gap-4 mt-3 text-sm flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-green-600 dark:text-green-400 font-medium">
                +{additions} additions
              </span>
              <span className="text-red-600 dark:text-red-400 font-medium">
                -{deletions} deletions
              </span>
            </div>

            {(splitMarkers.length > 0 || unifiedMarkers.length > 0) && (
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block h-2 w-2 rounded-sm bg-green-500" />
                  added
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block h-2 w-2 rounded-sm bg-red-500" />
                  removed
                </span>
                <span>Use the rail to jump to changes</span>
              </div>
            )}
          </div>
        </DialogHeader>

        <div className="flex-1 min-h-0 px-6 py-4 bg-muted/30">
          <div className="diff-container rounded-lg border bg-background overflow-hidden h-full">
            {viewType === 'split' ? (
              <div className="flex flex-col h-full">
                <div className="flex shrink-0 border-b bg-muted/50">
                  <div
                    className={cn(
                      'flex-1 px-3 py-2 text-xs font-medium border-r',
                      isDriveConflict && newerSide === 'left' && 'bg-amber-50/80 dark:bg-amber-950/20',
                    )}
                  >
                    {leftColumnLabel}
                    {isDriveConflict && newerSide === 'left' ? (
                      <span className="ml-2 text-[10px] uppercase tracking-wide text-amber-700 dark:text-amber-300">
                        newer
                      </span>
                    ) : null}
                  </div>
                  <div
                    className={cn(
                      'flex-1 px-3 py-2 text-xs font-medium pr-4',
                      isDriveConflict && newerSide === 'right' && 'bg-amber-50/80 dark:bg-amber-950/20',
                    )}
                  >
                    {rightColumnLabel}
                    {isDriveConflict && newerSide === 'right' ? (
                      <span className="ml-2 text-[10px] uppercase tracking-wide text-amber-700 dark:text-amber-300">
                        newer
                      </span>
                    ) : null}
                  </div>
                </div>
                <DiffScrollPane markers={splitMarkers} className="flex-1 min-h-0">
                  <div className="flex min-h-full">
                    <div className="flex-1 min-w-0 border-r p-2">
                      {originalLines.map((line, idx) => {
                        const isRemoved = changeIncludesLine(changes, 'removed', line)
                        return (
                          <div
                            key={`orig-${idx}`}
                            className={`font-mono text-sm py-0.5 px-2 border-l-2 ${
                              isRemoved
                                ? 'bg-red-100 dark:bg-red-950/30 text-red-900 dark:text-red-300 border-red-500'
                                : 'border-transparent'
                            }`}
                          >
                            <span className="select-none inline-block w-8 text-muted-foreground text-xs mr-2">
                              {idx + 1}
                            </span>
                            <span className="whitespace-pre-wrap break-words">{line || ' '}</span>
                          </div>
                        )
                      })}
                    </div>
                    <div className="flex-1 min-w-0 p-2">
                      {proposedLines.map((line, idx) => {
                        const isAdded = changeIncludesLine(changes, 'added', line)
                        return (
                          <div
                            key={`prop-${idx}`}
                            className={`font-mono text-sm py-0.5 px-2 border-l-2 ${
                              isAdded
                                ? 'bg-green-100 dark:bg-green-950/30 text-green-900 dark:text-green-300 border-green-500'
                                : 'border-transparent'
                            }`}
                          >
                            <span className="select-none inline-block w-8 text-muted-foreground text-xs mr-2">
                              {idx + 1}
                            </span>
                            <span className="whitespace-pre-wrap break-words">{line || ' '}</span>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </DiffScrollPane>
              </div>
            ) : (
              <DiffScrollPane markers={unifiedMarkers}>
                <UnifiedDiffView
                  original={diffOriginal}
                  proposed={diffProposed}
                  filename={filename}
                  showLineNumbers={true}
                  className="p-2"
                />
              </DiffScrollPane>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t bg-muted/20 space-y-3">
          {!readOnly && isMajorDeletion && (
            <div className="flex items-center gap-2 p-3 bg-amber-50 dark:bg-amber-950/20 border border-amber-500/30 rounded-md">
              <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
              <span className="text-sm font-medium text-amber-900 dark:text-amber-300">
                {isDriveConflict
                  ? `Keep Maater would add ${additions} lines to Drive and drop ${deletions} Drive-only lines (${Math.round(deletionPercentage)}% of the Drive column). Check the dates before deciding.`
                  : `Major rewrite: removes ${Math.round(deletionPercentage)}% of non-empty lines (${deletions} lines). Review carefully before accepting.`}
              </span>
            </div>
          )}
          {!readOnly && isDestructive && !isMajorDeletion && (
            <div className="flex items-center gap-2 p-3 bg-amber-50/60 dark:bg-amber-950/10 border border-amber-500/20 rounded-md">
              <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
              <span className="text-sm text-amber-900 dark:text-amber-300">
                {isDriveConflict
                  ? `Drive and Maater differ by about ${Math.round(deletionPercentage)}% of the Drive column (${deletions} Drive-only lines). Green on the right is the Keep Maater push.`
                  : `Removes about ${Math.round(deletionPercentage)}% of content (${deletions} lines). Accept if this matches your request.`}
              </span>
            </div>
          )}

          <div className="flex justify-end gap-2">
            {readOnly ? (
              <>
                <Button variant="outline" onClick={() => onOpenChange(false)} disabled={restoring}>
                  Close
                </Button>
                {onRestore && (
                  <Button onClick={onRestore} disabled={restoring} data-testid={testIds.diffPreviewDialog.restoreButton}>
                    <RotateCcw className="w-4 h-4 mr-2" />
                    {restoring ? 'Restoring...' : 'Restore this version'}
                  </Button>
                )}
              </>
            ) : conflictMode ? (
              <>
                <Button
                  variant="ghost"
                  onClick={handleCancel}
                  disabled={isApplying}
                  className="mr-auto"
                  data-testid={testIds.diffPreviewDialog.cancelButton}
                >
                  Cancel
                </Button>
                {isDriveConflict ? (
                  <>
                    <Button
                      variant={newerSide === 'right' ? 'default' : 'outline'}
                      onClick={() => onKeepFlowNotes?.()}
                      disabled={isApplying}
                      className={
                        newerSide === 'right' ? 'bg-green-600 hover:bg-green-700' : undefined
                      }
                      data-testid={testIds.diffPreviewDialog.keepLocalButton}
                    >
                      <Check className="w-4 h-4 mr-2" />
                      {isApplying && newerSide === 'right'
                        ? 'Keeping Maater...'
                        : `Keep Maater (${rightWhen})`}
                    </Button>
                    <Button
                      variant={newerSide === 'left' ? 'default' : 'outline'}
                      onClick={() => onAcceptUpstream?.()}
                      disabled={isApplying}
                      className={
                        newerSide === 'left' ? 'bg-green-600 hover:bg-green-700' : undefined
                      }
                      data-testid={testIds.diffPreviewDialog.acceptRemoteButton}
                    >
                      <Check className="w-4 h-4 mr-2" />
                      {isApplying && newerSide === 'left'
                        ? 'Using Drive...'
                        : `Use Google Drive (${leftWhen})`}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => onKeepFlowNotes?.()}
                      disabled={isApplying}
                      data-testid={testIds.diffPreviewDialog.keepLocalButton}
                    >
                      <X className="w-4 h-4 mr-2" />
                      Keep Maater
                    </Button>
                    <Button
                      onClick={() => onAcceptUpstream?.()}
                      disabled={isApplying}
                      className="bg-green-600 hover:bg-green-700"
                      data-testid={testIds.diffPreviewDialog.acceptRemoteButton}
                    >
                      <Check className="w-4 h-4 mr-2" />
                      {isApplying ? 'Applying...' : 'Accept Upstream'}
                    </Button>
                  </>
                )}
                {aiProposal != null && aiProposal !== '' && onAcceptAI ? (
                  <Button
                    variant="secondary"
                    onClick={() => onAcceptAI(aiProposal)}
                    disabled={isApplying}
                  >
                    Accept AI proposal
                  </Button>
                ) : null}
              </>
            ) : (
              <>
                {showBatchNav && (
                  <div className="flex items-center gap-1 mr-auto">
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={onBatchPrevious}
                      disabled={isApplying || batchIndex <= 0}
                      aria-label="Previous proposed change"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-xs text-muted-foreground tabular-nums px-1">
                      {batchIndex + 1} / {batchTotal}
                    </span>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={onBatchNext}
                      disabled={isApplying || batchIndex >= batchTotal - 1}
                      aria-label="Next proposed change"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}

                <Button
                  variant="outline"
                  onClick={() => onReject?.()}
                  disabled={isApplying}
                >
                  <X className="w-4 h-4 mr-2" />
                  Reject
                </Button>

                {onEdit && (
                  <Button
                    variant="outline"
                    onClick={onEdit}
                    disabled={isApplying}
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit First
                  </Button>
                )}

                <Button
                  onClick={() => onAccept?.()}
                  disabled={isApplying}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Check className="w-4 h-4 mr-2" />
                  {isApplying ? 'Applying...' : 'Accept Changes'}
                </Button>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
